import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import Supercluster from 'supercluster';
import api from '@utils/api';
import { useLocation } from '@context/LocationContext';
import { useTheme } from '@context/ThemeContext';
import { useAuth } from '@context/AuthContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import { useSocket } from '@hooks/useSocket';
import {
  getCategoryInfo,
  calculateDistance,
  formatDistance,
  formatActivityDate,
  formatYaarsInAndSpotsLeft,
  isActivityListingEnded,
} from '@utils/helpers';
import { clearPendingJoinIntent, readPendingJoinIntent, setPendingJoinIntent } from '@utils/pendingJoinIntent';
import GooglePlacesSearch from './GooglePlacesSearch';
import MapFiltersPanel from './MapFiltersPanel';
import MapCardCarousel from './MapCardCarousel';
import ActivitySafetyBadges from '@components/activity/ActivitySafetyBadges';
import FaceVerificationModal from '@components/safety/FaceVerificationModal';
import JoinLoginPromptModal from '@components/auth/JoinLoginPromptModal';
import Spinner from '@components/ui/Spinner';
import {
  Search,
  SlidersHorizontal,
  Crosshair,
  RotateCw,
  MapPin,
  Clock,
  Users,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { trackActivityJoin } from '@utils/analytics';

// ─── Marker DOM helpers ──────────────────────────────────────────────

function createUserLocationEl() {
  const el = document.createElement('div');
  el.innerHTML =
    '<div style="position:relative;width:20px;height:20px;">' +
    '<div style="position:absolute;inset:0;border-radius:50%;background:#3b82f6;box-shadow:0 0 0 4px rgba(59,130,246,0.2),0 2px 8px rgba(59,130,246,0.4);"></div>' +
    '<div style="position:absolute;inset:3px;border-radius:50%;border:2px solid white;"></div>' +
    '<div style="position:absolute;inset:-10px;border-radius:50%;background:rgba(59,130,246,0.08);animation:pfPulse 3s ease-in-out infinite;"></div>' +
    '</div>';
  return el;
}

function createActivityEl(props, isSelected, isDark) {
  const info = getCategoryInfo(props.category);
  const el = document.createElement('div');
  el.style.cssText =
    'display:flex;flex-direction:column;align-items:center;cursor:pointer;transition:transform 0.25s ease;';

  const pill = document.createElement('div');

  if (isSelected) {
    pill.style.cssText =
      'display:flex;align-items:center;gap:5px;' +
      'background:linear-gradient(135deg,#3b82f6,#7c3aed);' +
      'padding:7px 12px 7px 9px;border-radius:22px;' +
      'box-shadow:0 4px 20px rgba(59,130,246,0.45),0 0 0 2.5px #fff;' +
      'font-family:system-ui,-apple-system,sans-serif;white-space:nowrap;';
    pill.innerHTML =
      `<span style="font-size:16px;line-height:1;">${info.emoji}</span>` +
      `<span style="font-size:11px;font-weight:700;color:#fff;letter-spacing:-0.01em;">${info.label}</span>`;
    el.style.zIndex = '10';
    el.style.transform = 'scale(1.1)';
  } else {
    const bg = isDark ? '#1e293b' : '#ffffff';
    const shadow = isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.12)';
    const ring = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    pill.style.cssText =
      `display:flex;align-items:center;justify-content:center;` +
      `background:${bg};padding:7px;border-radius:50%;` +
      `box-shadow:0 2px 8px ${shadow},0 0 0 1px ${ring};` +
      `font-family:system-ui,-apple-system,sans-serif;transition:box-shadow 0.2s ease;`;
    pill.innerHTML = `<span style="font-size:16px;line-height:1;">${info.emoji}</span>`;
    el.style.zIndex = '1';

    el.addEventListener('mouseenter', () => {
      el.style.transform = 'scale(1.15)';
      pill.style.boxShadow = `0 4px 14px ${shadow},0 0 0 2px rgba(59,130,246,0.3)`;
    });
    el.addEventListener('mouseleave', () => {
      el.style.transform = 'scale(1)';
      pill.style.boxShadow = `0 2px 8px ${shadow},0 0 0 1px ${ring}`;
    });
  }

  el.appendChild(pill);
  return el;
}

function createClusterEl(count) {
  const size = count < 10 ? 38 : count < 50 ? 46 : 54;
  const el = document.createElement('div');
  el.style.cssText =
    `display:flex;align-items:center;justify-content:center;` +
    `width:${size}px;height:${size}px;border-radius:50%;` +
    `background:linear-gradient(135deg,#3b82f6,#6366f1);` +
    `color:#fff;font-size:${size < 46 ? 13 : 15}px;font-weight:800;` +
    `box-shadow:0 3px 12px rgba(59,130,246,0.35),0 0 0 3px rgba(255,255,255,0.85);` +
    `cursor:pointer;font-family:system-ui,-apple-system,sans-serif;transition:transform 0.2s ease;`;
  el.textContent = count > 99 ? '99+' : count;

  el.addEventListener('mouseenter', () => {
    el.style.transform = 'scale(1.12)';
  });
  el.addEventListener('mouseleave', () => {
    el.style.transform = 'scale(1)';
  });
  return el;
}

// ─── Main component ──────────────────────────────────────────────────

const ActivityRadarMap = () => {
  const routerLocation = useRouterLocation();
  const { mapboxToken } = usePublicConfig();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const userMarkerRef = useRef(null);
  const markersRef = useRef(new Map());
  const clusterRef = useRef(null);
  const debounceRef = useRef(null);
  const moveStartRef = useRef(null);
  const activitiesRef = useRef([]);
  const selectedRef = useRef(null);
  const darkRef = useRef(false);

  const { location: userLoc, locationLoading } = useLocation();
  const { dark } = useTheme();
  const { user } = useAuth();
  const { on } = useSocket();

  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    radiusKm: 5,
    category: '',
    allowedGender: '',
    verifiedActivitiesOnly: false,
    verifiedHostOnly: false,
  });
  const [showSearchArea, setShowSearchArea] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const [joinLoginModal, setJoinLoginModal] = useState(false);
  const [joinLoginActivityId, setJoinLoginActivityId] = useState(null);
  const pendingAutoJoinAttemptedRef = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    activitiesRef.current = activities;
  }, [activities]);
  useEffect(() => {
    selectedRef.current = selectedActivity;
  }, [selectedActivity]);
  useEffect(() => {
    darkRef.current = dark;
  }, [dark]);

  // ── Supercluster init ─────────────────────────────────────────────
  useEffect(() => {
    clusterRef.current = new Supercluster({
      radius: 60,
      maxZoom: 16,
    });
  }, []);

  // ── Cluster data + rendering ──────────────────────────────────────
  const loadClusterData = useCallback((acts) => {
    if (!clusterRef.current) return;
    const features = acts
      .filter((a) => a.location?.coordinates)
      .map((a) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: a.location.coordinates },
        properties: {
          _id: a._id,
          category: a.category,
          emoji: a.emoji,
          title: a.title,
        },
      }));
    clusterRef.current.load(features);
  }, []);

  const getVisibleClusters = useCallback(() => {
    const map = mapRef.current;
    const sc = clusterRef.current;
    if (!map || !sc) return [];
    try {
      const b = map.getBounds();
      return sc.getClusters(
        [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
        Math.floor(map.getZoom())
      );
    } catch {
      return [];
    }
  }, []);

  const renderMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const clusters = getVisibleClusters();
    const keep = new Set();

    clusters.forEach((f) => {
      const isCluster = !!f.properties.cluster;
      const id = isCluster
        ? `c-${f.properties.cluster_id}`
        : `p-${f.properties._id}`;
      keep.add(id);

      if (markersRef.current.has(id)) {
        markersRef.current.get(id).marker.setLngLat(f.geometry.coordinates);
        return;
      }

      let el;
      if (isCluster) {
        el = createClusterEl(f.properties.point_count);
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          const z = clusterRef.current.getClusterExpansionZoom(
            f.properties.cluster_id
          );
          map.easeTo({
            center: f.geometry.coordinates,
            zoom: Math.min(z, 17),
          });
        });
      } else {
        const actId = f.properties._id;
        const isSel = selectedRef.current?._id === actId;
        el = createActivityEl(f.properties, isSel, darkRef.current);
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          const prev = selectedRef.current;
          if (prev?._id === actId) {
            setSelectedActivity(null);
          } else {
            const act = activitiesRef.current.find((a) => a._id === actId);
            if (act) setSelectedActivity(act);
          }
        });
      }

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat(f.geometry.coordinates)
        .addTo(map);
      markersRef.current.set(id, { marker, el, isCluster });
    });

    markersRef.current.forEach((val, id) => {
      if (!keep.has(id)) {
        val.marker.remove();
        markersRef.current.delete(id);
      }
    });
  }, [getVisibleClusters]);

  const clearAndRender = useCallback(() => {
    markersRef.current.forEach((v) => v.marker.remove());
    markersRef.current.clear();
    renderMarkers();
  }, [renderMarkers]);

  // ── Fetch activities ──────────────────────────────────────────────
  const fetchActivities = useCallback(
    async (bounds) => {
      if (!bounds) return;
      try {
        setLoading(true);
        const params = {
          nelat: bounds.getNorthEast().lat,
          nelng: bounds.getNorthEast().lng,
          swlat: bounds.getSouthWest().lat,
          swlng: bounds.getSouthWest().lng,
          limit: 100,
        };
        if (filters.category) params.category = filters.category;
        if (filters.allowedGender)
          params.allowedGender = filters.allowedGender;
        if (filters.verifiedActivitiesOnly)
          params.verifiedActivitiesOnly = 'true';
        if (filters.verifiedHostOnly) params.verifiedHostOnly = 'true';

        const { data } = await api.get('/activities/nearby', { params });
        const all = data.activities || [];

        const filtered = all.filter((a) => {
          if (!a.location?.coordinates) return false;
          const dist = calculateDistance(
            userLoc.lat,
            userLoc.lng,
            a.location.coordinates[1],
            a.location.coordinates[0]
          );
          return dist <= filters.radiusKm;
        });

        setActivities(filtered);
        setShowSearchArea(false);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    },
    [filters, userLoc]
  );

  // ── Map initialization ────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || locationLoading || !mapboxToken) return;

    mapboxgl.accessToken = mapboxToken;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: dark
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/streets-v12',
      center: [userLoc.lng, userLoc.lat],
      zoom: 13,
      attributionControl: false,
      maxZoom: 18,
      minZoom: 2,
    });

    map.addControl(
      new mapboxgl.AttributionControl({ compact: true }),
      'bottom-right'
    );

    userMarkerRef.current = new mapboxgl.Marker({
      element: createUserLocationEl(),
    })
      .setLngLat([userLoc.lng, userLoc.lat])
      .addTo(map);

    map.on('load', () => {
      setMapReady(true);
      fetchActivities(map.getBounds());
    });

    map.on('movestart', () => {
      moveStartRef.current = map.getCenter();
    });

    map.on('moveend', () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        renderMarkers();
        const start = moveStartRef.current;
        if (start) {
          const dist = start.distanceTo(map.getCenter());
          if (dist > 1000) setShowSearchArea(true);
        }
      }, 80);
    });

    map.on('zoom', () => renderMarkers());

    mapRef.current = map;

    return () => {
      clearTimeout(debounceRef.current);
      markersRef.current.forEach((m) => m.marker.remove());
      markersRef.current.clear();
      if (userMarkerRef.current) userMarkerRef.current.remove();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationLoading, mapboxToken]);

  // ── Theme switch ──────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    const style = dark
      ? 'mapbox://styles/mapbox/dark-v11'
      : 'mapbox://styles/mapbox/streets-v12';
    mapRef.current.setStyle(style);
    mapRef.current.once('style.load', clearAndRender);
  }, [dark, mapReady, clearAndRender]);

  // ── Sync activities → Supercluster → markers ─────────────────────
  useEffect(() => {
    loadClusterData(activities);
    if (mapReady) clearAndRender();
  }, [activities, loadClusterData, mapReady, clearAndRender]);

  // ── Selection change → re-render markers ──────────────────────────
  useEffect(() => {
    if (mapReady) clearAndRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActivity?._id]);

  // ── Filter change → fit map to radius and refetch ─────────────────
  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    const map = mapRef.current;
    const km = filters.radiusKm;

    const latOffset = km / 111.32;
    const lngOffset = km / (111.32 * Math.cos((userLoc.lat * Math.PI) / 180));

    map.fitBounds(
      [
        [userLoc.lng - lngOffset, userLoc.lat - latOffset],
        [userLoc.lng + lngOffset, userLoc.lat + latOffset],
      ],
      { padding: 40, duration: 600 }
    );

    map.once('moveend', () => {
      fetchActivities(map.getBounds());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, mapReady]);

  // ── Desktop hover → scale marker ─────────────────────────────────
  useEffect(() => {
    markersRef.current.forEach((val, id) => {
      if (val.isCluster) return;
      const actId = id.replace('p-', '');
      const isHov = hoveredId === actId;
      const isSel = selectedRef.current?._id === actId;
      if (!isSel) {
        val.el.style.transform = isHov ? 'scale(1.2)' : 'scale(1)';
        val.el.style.zIndex = isHov ? '5' : '1';
      }
    });
  }, [hoveredId]);

  // ── Socket events ─────────────────────────────────────────────────
  useEffect(
    () =>
      on('activity_created', (activity) => {
        setActivities((prev) => {
          if (prev.some((a) => a._id === activity._id)) return prev;
          return [activity, ...prev];
        });
      }),
    [on]
  );

  useEffect(
    () =>
      on('activity_expired', (activityId) => {
        setActivities((prev) => prev.filter((a) => a._id !== activityId));
        setSelectedActivity((prev) =>
          prev?._id === activityId ? null : prev
        );
      }),
    [on]
  );

  // ── Handlers ──────────────────────────────────────────────────────
  const handleSearchArea = useCallback(() => {
    if (!mapRef.current) return;
    setShowSearchArea(false);
    fetchActivities(mapRef.current.getBounds());
  }, [fetchActivities]);

  const handlePlaceSelect = useCallback(
    ({ lat, lng }) => {
      if (!mapRef.current) return;
      mapRef.current.flyTo({ center: [lng, lat], zoom: 14, duration: 1500 });
      mapRef.current.once('moveend', () => {
        fetchActivities(mapRef.current.getBounds());
      });
    },
    [fetchActivities]
  );

  const handleCardSelect = useCallback((activity) => {
    setSelectedActivity((prev) =>
      prev?._id === activity._id ? null : activity
    );
    if (mapRef.current && activity.location?.coordinates) {
      mapRef.current.easeTo({
        center: activity.location.coordinates,
        duration: 600,
        padding: { bottom: 220 },
      });
    }
  }, []);

  const handleRecenter = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.flyTo({
      center: [userLoc.lng, userLoc.lat],
      zoom: 13,
      duration: 1000,
    });
    setShowSearchArea(false);
    mapRef.current.once('moveend', () => {
      fetchActivities(mapRef.current.getBounds());
    });
  }, [userLoc, fetchActivities]);

  const handleJoin = useCallback(
    async (activityId, { verifiedOnly, category } = {}) => {
      if (!user) {
        const path = `${routerLocation.pathname}${routerLocation.search}${routerLocation.hash}`;
        setPendingJoinIntent({ activityId, path });
        setJoinLoginActivityId(String(activityId));
        setJoinLoginModal(true);
        return;
      }
      if (verifiedOnly && !user.faceVerified) {
        setVerifyModal(true);
        return;
      }
      try {
        await api.post(`/activities/${activityId}/join`);
        trackActivityJoin({ activityId, category });
        toast.success("You're in!");
        if (mapRef.current) fetchActivities(mapRef.current.getBounds());
      } catch (err) {
        const code = err.response?.data?.code;
        if (
          code === 'FACE_VERIFICATION_REQUIRED' ||
          code === 'VERIFIED_ONLY_ACTIVITY'
        )
          setVerifyModal(true);
        toast.error(err.response?.data?.message || 'Failed to join');
      }
    },
    [user, fetchActivities, routerLocation.pathname, routerLocation.search, routerLocation.hash],
  );

  useEffect(() => {
    pendingAutoJoinAttemptedRef.current = false;
  }, [routerLocation.pathname, routerLocation.search, routerLocation.hash, user?._id]);

  /** After login/register, complete join started as a guest on this map URL. */
  useEffect(() => {
    if (!user || loading) return;
    if (pendingAutoJoinAttemptedRef.current) return;
    const pendingJoin = readPendingJoinIntent();
    if (!pendingJoin?.activityId) return;
    const here = `${routerLocation.pathname}${routerLocation.search}${routerLocation.hash}`;
    if (pendingJoin.path !== here) return;

    pendingAutoJoinAttemptedRef.current = true;
    const activityId = String(pendingJoin.activityId);
    const act = activities.find((a) => String(a._id) === activityId);

    const run = async () => {
      if (act) {
        const alreadyParticipant = act.participants?.some((p) => String(p._id || p) === String(user._id));
        if (alreadyParticipant || act.status === 'cancelled' || isActivityListingEnded(act, new Date())) {
          clearPendingJoinIntent();
          return;
        }
        if (act.verifiedOnly && !user.faceVerified) {
          setVerifyModal(true);
          return;
        }
      }

      try {
        await api.post(`/activities/${activityId}/join`);
        trackActivityJoin({ activityId, category: act?.category });
        clearPendingJoinIntent();
        toast.success("You're in!");
        if (mapRef.current) fetchActivities(mapRef.current.getBounds());
      } catch (err) {
        const code = err.response?.data?.code;
        const msg = err.response?.data?.message || 'Failed to join';
        if (code === 'FACE_VERIFICATION_REQUIRED' || code === 'VERIFIED_ONLY_ACTIVITY') {
          setVerifyModal(true);
          return;
        }
        clearPendingJoinIntent();
        toast.error(msg);
      }
    };

    run();
  }, [
    user,
    loading,
    activities,
    fetchActivities,
    routerLocation.pathname,
    routerLocation.search,
    routerLocation.hash,
  ]);

  // ── Error / loading states ────────────────────────────────────────

  if (!mapboxToken) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-surface-50 dark:bg-surface-950">
        <div className="text-center max-w-xs px-6">
          <div className="text-4xl mb-3">🗺️</div>
          <h3 className="text-sm font-bold text-surface-800 dark:text-white mb-1">
            Map not configured
          </h3>
          <p className="text-xs text-surface-500">
            Set{' '}
            <code className="bg-surface-100 dark:bg-surface-800 px-1 py-0.5 rounded text-[10px]">
              MAPBOX_TOKEN
            </code>{' '}
            in the server{' '}
            <code className="bg-surface-100 dark:bg-surface-800 px-1 py-0.5 rounded text-[10px]">
              .env
            </code>{' '}
            file and restart the API.
          </p>
        </div>
      </div>
    );
  }

  if (locationLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-surface-50 dark:bg-surface-950">
        <div className="text-center">
          <Spinner size="lg" className="mb-4" />
          <p className="text-sm font-medium text-surface-500">
            Detecting your location...
          </p>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="h-full w-full flex relative">
      {/* ═══════ DESKTOP LEFT PANEL ═══════ */}
      <aside className="hidden lg:flex lg:flex-col lg:w-[380px] lg:shrink-0 border-r border-surface-200/80 bg-white dark:bg-surface-900 dark:border-surface-800">
        {/* Search */}
        <div className="p-3 border-b border-surface-100 dark:border-surface-800">
          <GooglePlacesSearch onPlaceSelect={handlePlaceSelect} />
        </div>

        {/* Filters bar + count */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-surface-100 dark:border-surface-800">
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className="flex items-center gap-1.5 rounded-xl border border-surface-200 bg-surface-50 px-3 py-1.5 text-xs font-bold text-surface-700 transition-colors hover:bg-surface-100 dark:border-surface-700 dark:bg-surface-800 dark:text-surface-300 dark:hover:bg-surface-700"
          >
            <SlidersHorizontal size={13} className="text-brand-500" />
            Filters
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-1 text-xs font-semibold text-surface-500">
            {loading && (
              <RotateCw
                size={10}
                className="animate-spin text-brand-500"
              />
            )}
            <span className="tabular-nums">{activities.length}</span>
            <span className="text-surface-400">nearby</span>
          </div>
        </div>

        {/* Activity list */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {activities.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <div className="text-3xl mb-2">🔍</div>
              <p className="text-sm font-semibold text-surface-600 dark:text-surface-400">
                Be the first yaar. Start a vibe.
              </p>
              <p className="text-xs text-surface-400 mt-1">
                Pan the map or loosen filters — then start one for nearby yaars.
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {activities.map((activity) => {
                const isSelected =
                  selectedActivity?._id === activity._id;
                const isHovered = hoveredId === activity._id;
                const dist = activity.location?.coordinates
                  ? calculateDistance(
                      userLoc.lat,
                      userLoc.lng,
                      activity.location.coordinates[1],
                      activity.location.coordinates[0]
                    )
                  : null;
                const spotsLeft =
                  activity.maxParticipants -
                  (activity.participants?.length || 0);
                const isLive =
                  activity.status === 'live' || activity.isQuick;

                const thumbUrl =
                  activity.image && String(activity.image).trim() !== ''
                    ? String(activity.image).trim()
                    : '';

                return (
                  <div
                    key={activity._id}
                    onClick={() => handleCardSelect(activity)}
                    onMouseEnter={() => setHoveredId(activity._id)}
                    onMouseLeave={() => setHoveredId(null)}
                    className={`group flex items-start gap-3 rounded-xl p-3 cursor-pointer transition-all duration-150 ${
                      isSelected
                        ? 'bg-brand-50 border border-brand-200 dark:bg-brand-500/10 dark:border-brand-500/30'
                        : isHovered
                          ? 'bg-surface-50 dark:bg-surface-800/50'
                          : 'hover:bg-surface-50/80 dark:hover:bg-surface-800/30'
                    }`}
                  >
                    {thumbUrl ? (
                      <div
                        className="shrink-0 h-10 w-10 rounded-xl bg-cover bg-center ring-1 ring-black/8 dark:ring-white/10"
                        style={{ backgroundImage: `url(${thumbUrl})` }}
                        role="img"
                        aria-hidden
                      />
                    ) : (
                      <div
                        className={`shrink-0 flex h-10 w-10 items-center justify-center rounded-xl text-lg ${
                          isLive
                            ? 'bg-gradient-to-br from-amber-500/15 to-red-500/15'
                            : 'bg-gradient-to-br from-brand-500/10 to-violet-500/10'
                        }`}
                      >
                        {activity.emoji ||
                          getCategoryInfo(activity.category).emoji}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h4 className="text-sm font-bold text-surface-900 dark:text-white truncate">
                          {activity.title}
                        </h4>
                        {isLive && (
                          <span className="shrink-0 inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-amber-500 to-red-500 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                            <Zap
                              size={8}
                              className="fill-white"
                            />
                            LIVE
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 text-[11px] text-surface-500 dark:text-surface-400">
                        {dist != null && (
                          <span className="inline-flex items-center gap-0.5">
                            <MapPin
                              size={10}
                              className="text-brand-400"
                            />
                            {formatDistance(dist)}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-0.5">
                          <Clock
                            size={10}
                            className="text-violet-400"
                          />
                          {formatActivityDate(activity.dateTime)}
                        </span>
                        <span className="inline-flex min-w-0 items-center gap-0.5">
                          <Users
                            size={10}
                            className="shrink-0 text-emerald-400"
                          />
                          <span className="min-w-0 truncate">
                            {formatYaarsInAndSpotsLeft(
                              activity.participants?.length || 0,
                              activity.maxParticipants,
                            )}
                          </span>
                        </span>
                      </div>
                      <div className="mt-1.5">
                        <ActivitySafetyBadges
                          activity={activity}
                          creator={activity.creator}
                        />
                      </div>
                    </div>
                    {spotsLeft > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoin(activity._id, {
                            verifiedOnly: activity.verifiedOnly === true,
                            category: activity.category,
                          });
                        }}
                        className={`shrink-0 self-center rounded-lg px-3 py-1.5 text-[11px] font-bold text-white transition-all active:scale-95 ${
                          isLive
                            ? 'bg-gradient-to-r from-amber-500 to-red-500'
                            : 'bg-brand-500 hover:bg-brand-600'
                        }`}
                      >
                        Join the Vibe
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      {/* ═══════ MAP CONTAINER ═══════ */}
      <div className="flex-1 relative min-w-0 min-h-0">
        <div ref={mapContainerRef} className="absolute inset-0" />

        {/* Mobile top controls */}
        <div className="lg:hidden absolute top-0 left-0 right-0 z-10 pointer-events-none">
          <div className="p-3 pointer-events-auto">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <GooglePlacesSearch onPlaceSelect={handlePlaceSelect} />
              </div>
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="flex shrink-0 items-center justify-center h-[42px] w-[42px] rounded-xl border border-surface-200/70 bg-white/95 text-surface-700 shadow-sm backdrop-blur-xl transition-all hover:shadow-md dark:border-surface-700/50 dark:bg-surface-900/95 dark:text-surface-200"
              >
                <SlidersHorizontal size={16} className="text-brand-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Desktop top-right badge */}
        <div className="hidden lg:flex absolute top-3 right-3 z-10 items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-xl bg-white/95 border border-surface-200/70 px-3 py-2 text-xs font-semibold text-surface-600 shadow-sm backdrop-blur-xl dark:bg-surface-900/95 dark:text-surface-400 dark:border-surface-700/50">
            {loading && (
              <RotateCw
                size={11}
                className="animate-spin text-brand-500"
              />
            )}
            <span className="tabular-nums">{activities.length}</span>
            <span className="text-surface-400">nearby</span>
          </div>
        </div>

        {/* "Search this area" button */}
        {showSearchArea && (
          <div className="absolute top-16 lg:top-3 left-1/2 -translate-x-1/2 z-10">
            <button
              type="button"
              onClick={handleSearchArea}
              className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2.5 text-xs font-bold text-surface-800 shadow-lg border border-surface-200/60 backdrop-blur-xl transition-all hover:shadow-xl hover:scale-105 active:scale-95 dark:bg-surface-800 dark:text-white dark:border-surface-700"
            >
              <Search size={13} className="text-brand-500" />
              Search this area
            </button>
          </div>
        )}

        {/* Recenter button */}
        <div className="absolute bottom-24 lg:bottom-4 right-3 z-10">
          <button
            type="button"
            onClick={handleRecenter}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/95 text-surface-600 shadow-lg border border-surface-200/60 backdrop-blur-xl transition-all duration-300 hover:shadow-xl hover:bg-white active:scale-95 dark:bg-surface-900/95 dark:text-surface-300 dark:border-surface-700/50"
            aria-label="Center on my location"
          >
            <Crosshair size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Mobile card carousel */}
        <MapCardCarousel
          activities={activities}
          selectedActivity={selectedActivity}
          onSelect={handleCardSelect}
          onJoin={handleJoin}
          userLoc={userLoc}
        />

        {/* Loading overlay (initial) */}
        {loading && activities.length === 0 && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 dark:bg-surface-950/60 backdrop-blur-sm">
            <div className="text-center">
              <Spinner size="lg" className="mb-3" />
              <p className="text-sm font-semibold text-surface-600 dark:text-surface-400">
                Finding vibes nearby...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Filters panel */}
      <MapFiltersPanel
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        onChange={setFilters}
      />

      {/* Face verification modal */}
      <FaceVerificationModal
        open={verifyModal}
        onClose={() => setVerifyModal(false)}
        variant="join"
      />

      <JoinLoginPromptModal
        open={joinLoginModal}
        onClose={() => {
          setJoinLoginModal(false);
          setJoinLoginActivityId(null);
        }}
        activityId={joinLoginActivityId}
      />
    </div>
  );
};

export default ActivityRadarMap;
